import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Customer } from '../../types/customer';
import {
  ClipboardDocumentListIcon, WrenchScrewdriverIcon, DocumentTextIcon, ShoppingCartIcon, TruckIcon, BanknotesIcon,
  FolderIcon, PhoneIcon, EnvelopeIcon, ChatBubbleLeftRightIcon, LifebuoyIcon, ShieldCheckIcon, DocumentCheckIcon,
  ArrowTopRightOnSquareIcon, ArrowPathIcon
} from '@heroicons/react/24/outline';

type Kind = 'task' | 'worksheet' | 'quotation' | 'order' | 'delivery' | 'invoice' | 'project' | 'deal' | 'log' | 'ticket' | 'warranty' | 'contract';

interface Ev {
  id: string;
  kind: Kind;
  date: Date;
  title: string;
  sub?: string;
  status?: string;
  tone?: 'ok' | 'warn' | 'bad' | 'muted' | 'info';
  amount?: number;
  currency?: string;
  url?: string;
}

const KIND: Record<Kind, { label: string; icon: React.ElementType; color: string }> = {
  task: { label: 'Feladat', icon: ClipboardDocumentListIcon, color: 'text-sky-600 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-300' },
  worksheet: { label: 'Munkalap', icon: WrenchScrewdriverIcon, color: 'text-primary-700 bg-primary-50 dark:bg-primary-900/30 dark:text-primary-300' },
  quotation: { label: 'Ajánlat', icon: DocumentTextIcon, color: 'text-violet-700 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-300' },
  order: { label: 'Megrendelés', icon: ShoppingCartIcon, color: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300' },
  delivery: { label: 'Szállítólevél', icon: TruckIcon, color: 'text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-300' },
  invoice: { label: 'Számla', icon: BanknotesIcon, color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300' },
  project: { label: 'Projekt', icon: FolderIcon, color: 'text-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300' },
  deal: { label: 'Kapcsolat', icon: PhoneIcon, color: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200' },
  log: { label: 'Napló', icon: ChatBubbleLeftRightIcon, color: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200' },
  ticket: { label: 'Bejelentés', icon: LifebuoyIcon, color: 'text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-300' },
  warranty: { label: 'Garancia', icon: ShieldCheckIcon, color: 'text-teal-700 bg-teal-50 dark:bg-teal-900/30 dark:text-teal-300' },
  contract: { label: 'Szerződés', icon: DocumentCheckIcon, color: 'text-slate-700 bg-slate-100 dark:bg-slate-700 dark:text-slate-200' }
};

const STATUS_HU: Record<string, string> = {
  todo: 'teendő', in_progress: 'folyamatban', completed: 'kész', cancelled: 'törölve', draft: 'vázlat', assigned: 'kiosztva', review_pending: 'ellenőrzésre vár',
  sent: 'elküldve', accepted: 'elfogadva', rejected: 'elutasítva', expired: 'lejárt', pending: 'függő', confirmed: 'visszaigazolva', processing: 'feldolgozás',
  shipped: 'kiszállítva', delivered: 'kézbesítve', PAID: 'fizetve', UNPAID: 'fizetetlen', OVERDUE: 'lejárt', CANCELLED: 'sztornó', DRAFT: 'vázlat', DEV: 'teszt',
  planning: 'tervezés', active: 'aktív', paused: 'szünetel', submitted: 'beküldve', waiting_customer: 'ügyfélre vár', signed: 'aláírva', terminated: 'megszűnt'
};
const toneOf = (s?: string): Ev['tone'] => {
  if (!s) return 'muted';
  if (['completed', 'accepted', 'PAID', 'delivered', 'signed', 'active', 'confirmed'].includes(s)) return 'ok';
  if (['cancelled', 'rejected', 'CANCELLED', 'expired', 'OVERDUE', 'terminated'].includes(s)) return 'bad';
  if (['in_progress', 'sent', 'UNPAID', 'processing', 'shipped', 'waiting_customer', 'assigned', 'review_pending'].includes(s)) return 'warn';
  return 'muted';
};
const TONE: Record<NonNullable<Ev['tone']>, string> = {
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  warn: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  bad: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  info: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  muted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
};

const toDate = (v: any): Date | undefined => {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v); return isNaN(d.getTime()) ? undefined : d;
};
const money = (n?: number, c = 'HUF') => n != null && !isNaN(n) ? new Intl.NumberFormat('hu-HU', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n) : '';
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayLabel = (d: Date) => {
  const now = new Date(); const y = new Date(now); y.setDate(y.getDate() - 1);
  if (sameDay(d, now)) return 'Ma';
  if (sameDay(d, y)) return 'Tegnap';
  return d.toLocaleDateString('hu-HU', { year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined, month: 'long', day: 'numeric', weekday: 'short' });
};

interface Props {
  customer: Customer;
}

/** Ügyfél idővonal: minden, ami az ügyféllel történt, egy listában, időrendben. */
const CustomerTimeline: React.FC<Props> = ({ customer }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = (customer as any).organizationId || (user as any)?.organizationId;
  const [events, setEvents] = useState<Ev[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Set<Kind>>(new Set());
  const [limitN, setLimitN] = useState(60);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const out: Ev[] = [];
      const fetch = async (col: string) => {
        try {
          const q = orgId ? query(collection(db, col), where('organizationId', '==', orgId), where('customerId', '==', customer.id)) : query(collection(db, col), where('customerId', '==', customer.id));
          return (await getDocs(q)).docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        } catch (e) {
          try { return (await getDocs(query(collection(db, col), where('customerId', '==', customer.id)))).docs.map(d => ({ id: d.id, ...(d.data() as any) })); } catch { return []; }
        }
      };
      const [tasks, worksheets, quotations, orders, deliveries, invoices, projects, deals, tickets, warranties, contracts] = await Promise.all([
        fetch('tasks'), fetch('worksheets'), fetch('quotations'), fetch('orders'), fetch('delivery_notes'), fetch('invoices'), fetch('projects'), fetch('deals'), fetch('customerTickets'), fetch('warranties'), fetch('customerContracts')
      ]);
      const push = (e: Omit<Ev, 'tone'> & { tone?: Ev['tone'] }) => out.push({ tone: e.tone ?? toneOf(e.status), ...e });

      for (const t of tasks) {
        if (t.isRecurringTemplate) continue;
        const created = toDate(t.createdAt); const done = toDate(t.completedDate);
        if (created) push({ id: `t-${t.id}`, kind: 'task', date: created, title: `${t.taskNumber || ''} ${t.title}`.trim(), sub: [t.assignedToName, t.dueDate ? `határidő ${toDate(t.dueDate)?.toLocaleDateString('hu-HU')}` : ''].filter(Boolean).join(' · '), status: t.status, url: `/tasks?task=${t.id}` });
        if (done && t.status === 'completed') push({ id: `td-${t.id}`, kind: 'task', date: done, title: `${t.taskNumber || ''} ${t.title} – elkészült`.trim(), sub: t.actualHours ? `${Math.round(t.actualHours * 10) / 10} óra` : undefined, status: 'completed', url: `/tasks?task=${t.id}` });
      }
      for (const w of worksheets) {
        const d = toDate(w.worksheetDate) || toDate(w.createdAt);
        if (d) push({ id: `w-${w.id}`, kind: 'worksheet', date: d, title: `${w.worksheetNumber || ''} ${w.title || ''}`.trim(), sub: [w.workDetails ? String(w.workDetails).replace(/\s+/g, ' ').slice(0, 90) : '', (w.photos || []).length ? `${w.photos.length} fotó` : '', (w.signatures || []).length ? 'aláírva' : ''].filter(Boolean).join(' · '), status: w.status, amount: (w.items || []).reduce((s: number, i: any) => s + (Number(i.totalGross) || 0), 0) || undefined, url: '/worksheets' });
        const fbAt = toDate(w.feedback?.at);
        if (w.feedback?.rating && fbAt) push({ id: `wf-${w.id}`, kind: 'worksheet', date: fbAt, title: `${'★'.repeat(w.feedback.rating)}${'☆'.repeat(5 - w.feedback.rating)} Ügyfél-értékelés – ${w.worksheetNumber || ''}`, sub: w.feedback.comment ? `„${String(w.feedback.comment).slice(0, 140)}”` : undefined, tone: w.feedback.rating >= 4 ? 'ok' : w.feedback.rating === 3 ? 'warn' : 'bad', status: `${w.feedback.rating}/5`, url: '/worksheets' });
      }
      for (const q of quotations) {
        const d = toDate(q.createdAt) || toDate(q.date);
        if (d) push({ id: `q-${q.id}`, kind: 'quotation', date: d, title: `${q.number || q.quotationNumber || ''} ${q.name || q.title || ''}`.trim(), status: q.status, amount: q.totalAmount, currency: q.currency || 'HUF', url: '/quotations' });
      }
      for (const o of orders) {
        const d = toDate(o.createdAt) || toDate(o.date);
        if (d) push({ id: `o-${o.id}`, kind: 'order', date: d, title: `${o.number || ''} ${o.name || ''}`.trim(), status: o.status, amount: o.totalAmount ?? o.total, currency: o.currency || 'HUF', url: '/orders' });
      }
      for (const dn of deliveries) {
        const d = toDate(dn.createdAt) || toDate(dn.date);
        if (d) push({ id: `d-${dn.id}`, kind: 'delivery', date: d, title: `${dn.number || ''} ${dn.name || ''}`.trim(), status: dn.status, url: '/delivery-notes' });
      }
      for (const i of invoices) {
        const d = toDate(i.issueDate) || toDate(i.createdAt);
        if (d) push({ id: `i-${i.id}`, kind: 'invoice', date: d, title: `${i.invoiceNumber || 'Számla'}${i.documentType === 'PROFORMA' ? ' (díjbekérő)' : ''}`, sub: [i.provider ? (i.provider === 'billingo' ? 'Billingo' : 'Számlázz.hu') : '', i.dueDate ? `fizetési határidő ${toDate(i.dueDate)?.toLocaleDateString('hu-HU')}` : ''].filter(Boolean).join(' · '), status: i.status, amount: i.grossAmount ?? i.total, currency: i.currency || 'HUF', url: '/billing' });
      }
      for (const p of projects) {
        const d = toDate(p.startDate) || toDate(p.createdAt);
        if (d) push({ id: `p-${p.id}`, kind: 'project', date: d, title: p.name, sub: p.endDate ? `– ${toDate(p.endDate)?.toLocaleDateString('hu-HU')}` : undefined, status: p.status, amount: p.budget, currency: p.currency || 'HUF', url: `/projects/${p.id}` });
      }
      for (const dl of deals) {
        const d = toDate(dl.date) || toDate(dl.createdAt);
        if (d) push({ id: `dl-${dl.id}`, kind: 'deal', date: d, title: `${dl.type === 'phone' ? 'Telefon' : dl.type === 'email' ? 'E-mail' : 'SMS'}${dl.subject ? `: ${dl.subject}` : ''}`, sub: String(dl.description || dl.outcome || '').replace(/\s+/g, ' ').slice(0, 120), status: dl.status, url: '/deals' });
      }
      for (const tk of tickets) {
        const d = toDate(tk.createdAt);
        if (d) push({ id: `tk-${tk.id}`, kind: 'ticket', date: d, title: tk.title || tk.subject || 'Bejelentés', sub: String(tk.description || '').replace(/\s+/g, ' ').slice(0, 100), status: tk.status, url: '/customer-tickets' });
      }
      for (const wr of warranties) {
        const d = toDate(wr.startDate) || toDate(wr.createdAt);
        if (d) push({ id: `wr-${wr.id}`, kind: 'warranty', date: d, title: `${wr.warrantyNumber || 'Garancia'} – ${wr.productName || ''}`, sub: wr.endDate ? `lejár ${toDate(wr.endDate)?.toLocaleDateString('hu-HU')}` : undefined, status: wr.status, url: '/warranty' });
      }
      for (const c of contracts) {
        const d = toDate(c.startDate) || toDate(c.createdAt);
        if (d) push({ id: `c-${c.id}`, kind: 'contract', date: d, title: c.title || c.name || c.contractNumber || 'Szerződés', sub: c.endDate ? `– ${toDate(c.endDate)?.toLocaleDateString('hu-HU')}` : undefined, status: c.status, url: '/contracts' });
      }
      for (const l of ((customer as any).relationshipLogs || []) as any[]) {
        const d = toDate(l.createdAt);
        if (d) push({ id: `l-${l.id || l.entryId}`, kind: 'log', date: d, title: String(l.content || '').replace(/\s+/g, ' ').slice(0, 160), sub: l.createdByName || undefined, tone: 'muted' });
      }
      if (!cancelled) { setEvents(out.sort((a, b) => b.date.getTime() - a.date.getTime())); setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [customer.id, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => { const m = new Map<Kind, number>(); events.forEach(e => m.set(e.kind, (m.get(e.kind) || 0) + 1)); return m; }, [events]);
  const visible = useMemo(() => (filter.size ? events.filter(e => filter.has(e.kind)) : events), [events, filter]);
  const shown = visible.slice(0, limitN);

  // Napok szerinti csoport
  const groups: { day: Date; items: Ev[] }[] = [];
  for (const e of shown) {
    const g = groups[groups.length - 1];
    if (g && sameDay(g.day, e.date)) g.items.push(e); else groups.push({ day: e.date, items: [e] });
  }

  const summary = useMemo(() => {
    const inv = events.filter(e => e.kind === 'invoice' && e.status !== 'CANCELLED' && e.status !== 'DRAFT' && e.status !== 'DEV');
    const paid = inv.filter(e => e.status === 'PAID').reduce((s, e) => s + (e.amount || 0), 0);
    const open = inv.filter(e => e.status === 'UNPAID' || e.status === 'OVERDUE').reduce((s, e) => s + (e.amount || 0), 0);
    const ws = events.filter(e => e.kind === 'worksheet').length;
    const openTasks = events.filter(e => e.kind === 'task' && !e.id.startsWith('td-') && (e.status === 'todo' || e.status === 'in_progress')).length;
    const last = events[0]?.date;
    const ratings = events.filter(e => e.id.startsWith('wf-')).map(e => Number(String(e.status).split('/')[0]) || 0).filter(Boolean);
    const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
    return { paid, open, ws, openTasks, last, avgRating, ratingCount: ratings.length };
  }, [events]);

  const toggle = (k: Kind) => setFilter(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div className="space-y-3">
      {/* Összegzés */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { l: summary.avgRating != null ? `Elégedettség (${summary.ratingCount} értékelés)` : 'Utolsó esemény', v: summary.avgRating != null ? `${'★'.repeat(Math.round(summary.avgRating))} ${summary.avgRating.toFixed(1)}` : (summary.last ? summary.last.toLocaleDateString('hu-HU') : '–') },
          { l: 'Nyitott feladat', v: String(summary.openTasks) },
          { l: 'Kifizetett számlák', v: money(summary.paid) || '–' },
          { l: 'Nyitott tartozás', v: summary.open ? money(summary.open) : '–', warn: summary.open > 0 }
        ].map(k => (
          <div key={k.l} className="crm-card px-3 py-2">
            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{k.l}</div>
            <div className={`text-base font-semibold tabular-nums truncate ${k.warn ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-gray-50'}`}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Szűrő chipek */}
      <div className="flex flex-wrap gap-1.5">
        {(Object.keys(KIND) as Kind[]).filter(k => counts.get(k)).map(k => {
          const on = filter.has(k); const Icon = KIND[k].icon;
          return (
            <button key={k} type="button" onClick={() => toggle(k)} className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-xs border transition-colors ${on ? 'border-gray-900 dark:border-white bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'border-[var(--crm-border)] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'}`}>
              <Icon className="w-3.5 h-3.5" />{KIND[k].label}<span className={`tabular-nums ${on ? 'opacity-70' : 'text-gray-400'}`}>{counts.get(k)}</span>
            </button>
          );
        })}
        {filter.size > 0 && <button type="button" onClick={() => setFilter(new Set())} className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white">Összes</button>}
      </div>

      {/* Idővonal */}
      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-2 w-full justify-center"><ArrowPathIcon className="w-4 h-4 animate-spin" />Betöltés…</div>
      ) : shown.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Még nincs esemény ennél az ügyfélnél.</div>
      ) : (
        <div className="relative">
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[var(--crm-border)]" />
          <div className="space-y-4">
            {groups.map(g => (
              <div key={g.day.toISOString()}>
                <div className="relative pl-9 mb-1.5">
                  <span className="absolute left-[11px] top-1.5 w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600 ring-4 ring-[var(--crm-surface)]" />
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{dayLabel(g.day)}</div>
                </div>
                <div className="space-y-1">
                  {g.items.map(e => {
                    const K = KIND[e.kind]; const Icon = K.icon;
                    return (
                      <div key={e.id} className="relative pl-9 group">
                        <span className={`absolute left-[5px] top-1 w-[22px] h-[22px] rounded-full inline-flex items-center justify-center ring-4 ring-[var(--crm-surface)] ${K.color}`}><Icon className="w-3.5 h-3.5" /></span>
                        <div className="crm-card px-3 py-1.5 flex items-start gap-2 min-w-0">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[10px] uppercase tracking-wide text-gray-400 flex-shrink-0">{K.label}</span>
                              <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{e.title}</span>
                              {e.status && <span className={`text-[10px] px-1.5 h-4 inline-flex items-center rounded flex-shrink-0 ${TONE[e.tone || 'muted']}`}>{STATUS_HU[e.status] || e.status}</span>}
                            </div>
                            {e.sub && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{e.sub}</div>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {e.amount != null && e.amount !== 0 && <span className="text-sm tabular-nums font-medium text-gray-900 dark:text-gray-100">{money(e.amount, e.currency)}</span>}
                            <span className="text-[11px] tabular-nums text-gray-400">{e.date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}</span>
                            {e.url && <button type="button" onClick={() => navigate(e.url!)} className="w-6 h-6 inline-flex items-center justify-center rounded text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 focus:opacity-100 max-sm:opacity-100" title="Megnyitás"><ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /></button>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {visible.length > shown.length && (
            <div className="pt-3 pl-9"><button type="button" onClick={() => setLimitN(n => n + 60)} className="crm-btn crm-btn-secondary h-8 text-xs">Továbbiak ({visible.length - shown.length})</button></div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerTimeline;
